import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logoIcon from './../assets/images/logo-black.svg';
import { Icon } from '@iconify/react';

interface Props {
    onOpenFeedbackModal: () => void;
    isNavOpen: boolean;
    isLaptop: boolean;
    toggleNav: () => void;
    hamburgerRef: React.RefObject<HTMLButtonElement>
}

function DashboardSideNav({ onOpenFeedbackModal, isNavOpen, isLaptop, toggleNav, hamburgerRef }: Props) {
    const navigate = useNavigate();
    const location = useLocation(); // Get the current location
    const navRef = useRef<HTMLDivElement>(null);

    const handleNavSelect = (path: string) => {
        navigate(path);
    };

    const isActive = (path: string, startsWith: boolean = false) => {
        return startsWith ? location.pathname.startsWith(path) : location.pathname === path;
    };

    const handleLogout = () => {
        localStorage.removeItem('tiller');
        navigate('/signin');
    };

    // Handle clicking outside the sidenav to close it, except when clicking on the hamburger button
    const handleClickOutside = (event: MouseEvent) => {
        // Check if click is inside the nav or the hamburger button
        if (
            navRef.current &&
            !navRef.current.contains(event.target as Node) &&
            hamburgerRef.current &&
            !hamburgerRef.current.contains(event.target as Node)
        ) {
            toggleNav();
        }
    };

    useEffect(() => {
        if (isNavOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isNavOpen]);

    return (
        <nav
            ref={navRef}
            className={`flex flex-col  justify-between  fixed  top-0 left-0 z-20 h-full bg-white md:shadow-sm lg:shadow-none text-gray-700 pt-2 pb-20 w-60 transition-transform duration-300 ease-in-out transform
${isNavOpen || isLaptop ? 'translate-x-0' : '-translate-x-full'}  lg:relative lg:translate-x-0`} // Dynamic transform
        >
            <div className='md:mt-10 lg:mt-0'>
                <div className="w-[77.96px] h-[18.39px] mt-6 ml-6 flex items-center justify-center">
                    <Link to="/">
                        <img src={logoIcon} alt="company logo" className="h-10 mr-3 md:hidden lg:block" />
                        <div className='text-2xl font-semibold lg:hidden md:block'>Menu</div>
                    </Link>
                </div>

                <div className="flex flex-col gap-1 mt-[30px] h-full">
                    <button
                        className={`nav-button ${isActive('/') ? 'active' : ''}`}
                        onClick={() => handleNavSelect('/')}
                    >
                        <Icon icon="mingcute:grid-2-line" className="nav-icon" /> Feed
                    </button>

                    <button
                        className={`nav-button ${isActive('/recordings', true) || isActive('/recording', true) ? 'active' : ''}`}
                        onClick={() => handleNavSelect('/recordings')}
                    >
                        <Icon icon="fluent-emoji-high-contrast:record-button" className="nav-icon" /> Recordings
                    </button>

                    <button
                        className={`nav-button ${isActive('/playbooks', true) ? 'active' : ''}`}
                        onClick={() => handleNavSelect('/playbooks')}
                    >
                        <Icon icon="akar-icons:chat-dots" className="nav-icon" /> Playbooks
                    </button>

                    <button
                        className={`nav-button ${isActive('/agents', true) ? 'active' : ''}`}
                        onClick={() => handleNavSelect('/agents')}
                    >
                        <Icon icon="solar:user-linear" className="nav-icon" /> Agents
                    </button>

                    <button
                        className={`nav-button ${isActive('/settings', true) ? 'active' : ''}`}
                        onClick={() => handleNavSelect('/settings')}
                    >
                        <Icon icon="hugeicons:settings-01" className="nav-icon" /> Settings
                    </button>
                </div>
            </div>

            <div className='flex flex-col gap-4 ml-6 text-sm'>
                
                <div>
                    <button className='text-[#585858] hover:underline' onClick={onOpenFeedbackModal}>Send feedback</button>
                </div>
                <div>
                    <button className='text-[#ED6459]' onClick={handleLogout}>Log out</button>
                </div>
            </div>
        </nav>
    );
}

export default DashboardSideNav;
